import { createFileRoute, redirect } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import {
  legacyProductsTarget,
  runProductsSearchSchema,
  type BatchProductsSearch,
} from '~/features/catalog-enrichment/utils/run-products-search';

const RunBatchProductsPage = lazy(
  () => import('~/features/catalog-enrichment/pages/run-batch-products-page'),
);

/**
 * The Products tab's product table (one row per product). Parses the old one-route schema so a
 * pre-split link (`?view=listings`, `?lens=review`, `?decision=`, a step drill…) is redirected to
 * the route that owns that view now — the address bar always names what is on screen.
 */
export const Route = createFileRoute(
  '/_authenticated/_tenant/catalog-enrichment/runs/$runId/products/',
)({
  validateSearch: runProductsSearchSchema,
  beforeLoad: ({ params, search }) => {
    const target = legacyProductsTarget(search);
    if (!target) return;
    const runId = params.runId;
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router's redirect
    throw target.to === 'review'
      ? redirect({
          to: '/catalog-enrichment/runs/$runId/gate',
          params: { runId },
          search: target.search,
          replace: true,
        })
      : target.to === 'listings'
        ? redirect({
            to: '/catalog-enrichment/runs/$runId/products/listings',
            params: { runId },
            search: target.search,
            replace: true,
          })
        : target.to === 'skipped'
          ? redirect({
              to: '/catalog-enrichment/runs/$runId/products/skipped',
              params: { runId },
              search: target.search,
              replace: true,
            })
          : redirect({
              to: '/catalog-enrichment/runs/$runId/products',
              params: { runId },
              search: target.search,
              replace: true,
            });
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { runId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  // Tabs and pages are history entries (Back undoes them); typing in search replaces the entry.
  const onSearchChange = (patch: Partial<BatchProductsSearch>, opts?: { replace?: boolean }) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: opts?.replace ?? false });
  };

  return (
    <Suspense fallback={<PageSkeleton />}>
      {/* Keyed by batch so switching batches never carries one batch's state into the next. */}
      <RunBatchProductsPage
        key={runId}
        runId={runId}
        search={search}
        onSearchChange={onSearchChange}
      />
    </Suspense>
  );
}
